import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Box, Typography, TextField, Select, MenuItem, FormControl, InputLabel,
  Button, IconButton, CircularProgress, Divider, Tooltip, Chip, LinearProgress, Alert,
  Autocomplete, createFilterOptions, Checkbox, FormControlLabel, FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import { enterAdvanceProps } from '../../utils/enterAdvance';
import FlexService from '../../services/FlexService';
import { resolveVisibleFieldNames, isFieldShownFor } from './fieldVisibility';
import InlineCreateDialog, {
  createOptionsByField, canCreateWith, isCreateEntry, createEntry,
} from './InlineCreateDialog';
import AddCustomFieldDialog, {
  canAddCustomField, withCustomFields, foldCustomFields, isCustomField,
} from './AddCustomFieldDialog';

const defaultFilterOptions = createFilterOptions();

// Attaches each field's createOption (from the config field list) to its metadata object.
const withCreateOptions = (fields, entries) => {
  const byField = createOptionsByField(entries);
  return fields.map((f) => (byField[f.fieldName] ? { ...f, createOption: byField[f.fieldName] } : f));
};

// -----------------------------------------------------------------
// Tiny helpers
// -----------------------------------------------------------------

// Walk a dot-path into an object. Returns undefined for missing segments.
// Example: getByPath({a:{b:[{c:1}]}}, 'a.b.0.c') === 1
const getByPath = (obj, path) => {
  if (!obj || !path) return undefined;
  return path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);
};

// Interpolate {{field}} (or {{a.b.c}}) references against a row.
const renderTemplate = (template, row) => {
  if (!template) return '';
  return String(template).replace(/\{\{([^}]+)\}\}/g, (_, key) => {
    const v = getByPath(row, key.trim());
    return v == null ? '' : String(v);
  });
};

// Type-coerce a value based on field metadata before sending to WMS.
// - NUMBER: '30' → 30 (MUI type="number" returns strings)
// - BOOLEAN: 'true' → true
// - DATE / DATETIME: 'yyyy-MM-dd' → 'yyyy-MM-ddT00:00:00Z' (WMS DateTimeUtil
//   requires a full ISO-8601 zoned timestamp; plain dates trigger NPE on
//   ZonedDateTime.parse + .format(null) on the WMS side)
const coerceFieldValue = (f, v) => {
  if (v === undefined || v === null || v === '') return v;
  if (f.fieldType === 'NUMBER') {
    const n = Number(v);
    return Number.isNaN(n) ? v : n;
  }
  if (f.fieldType === 'BOOLEAN') {
    return v === true || v === 'true';
  }
  if ((f.fieldType === 'DATE' || f.fieldType === 'DATETIME') && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) {
    return v + 'T00:00:00Z';
  }
  return v;
};

// Apply coerceFieldValue across an object using the field metadata list.
// Returns a new object with only the populated (non-empty) values.
const coercePayload = (fields, values) => {
  const out = {};
  fields.forEach((f) => {
    const v = coerceFieldValue(f, values[f.fieldName]);
    if (v !== undefined && v !== null && v !== '') out[f.fieldName] = v;
  });
  return out;
};

// A summary field's value; a custom field (FH-202) may sit in the record's customField map instead.
const summaryValue = (row, f) => {
  const v = row?.[f.fieldName];
  return v !== undefined ? v : getByPath(row, f.fieldName);
};

const isEmptyValue = (v) => v === undefined || v === null || v === '';

// Header-summary display: blank shows as "—"; a DATE field shows just its date, not the
// zoned timestamp the create payload carries ("2026-10-15T00:00:00Z" -> "2026-10-15").
const formatSummaryValue = (f, v) => {
  if (v === undefined || v === null || v === '') return '—';
  if (f.fieldType === 'DATE' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) return v.slice(0, 10);
  return v;
};

/**
 * RapidEntryOverlay — domain-neutral overlay for any "scan / submit / repeat" workflow.
 *
 * Every label, entity name, field name, response path, and template comes from `config`
 * (passed in as `action.rapidEntry` from the workspace JSON). The component has no
 * knowledge of receiving, picking, counting, or any other domain.
 *
 * Modes:
 *   1. Resolver phase — when no rowData is provided AND config.resolverPhase exists.
 *      Prompts for one of N inputs, runs resolverPhase.scriptName, lets the user pick
 *      one match (or auto-advances if exactly one is returned).
 *   2. Scan loop — header summary (read-only), progress panel (refetched after every
 *      submit), blank line form (auto-focus on scanFirst), recently-submitted tally
 *      with optional void, optional close button.
 *
 * Required config shape:
 *   {
 *     actionLabel?: the label of the button that opened the panel (added by the FORM/WIZARD
 *       action handler); the title of any step without its own title (FH-234)
 *     resolverPhase?: {
 *       scriptName, instructions?, fields: [{name,label,type,placeholder}],
 *       matchDisplay: { primaryField, subtitleTemplate },
 *       blindActionButton?: { enabled, label, tooltip }
 *     },
 *     headerSummary: { entity, fields: [fieldName, ...], refreshScript?, refreshKeyField? },
 *       (refreshScript is re-run with { [refreshKeyField]: value } after each submit/void so
 *        the summary shows changes such as a status that the submit itself caused)
 *     progressPanel: {
 *       title, scriptName, itemKeyField, displayField,
 *       expectedField, receivedField
 *     },
 *     lineEntry: {
 *       entity, submitScript, scanFirst, repeatOnSubmit, fields: [fieldName, ...],
 *       submitLabel?, itemFacts?: { script, dependOn? }
 *     },
 *       contextDefaults?: [{ whenField, set: { fieldName: value } }]: when the header record (the receipt) has
 *       whenField filled in, the line starts with these values instead of the fields' own defaults (FH-411: a
 *       return receipt receives on Hold - Inspection, as the receive will set it).
 *       itemFacts: a Command run whenever the line's dependOn field (itemId) changes; the flat map it returns
 *       (e.g. { itemLotTracked: 'true' }) is read by visibleWhenField like the line's own values, so a question
 *       the item doesn't need (lot, expiry, serial, case labels) isn't shown, and a required one is asked.
 *     tally: {
 *       title, limit, responseRowsPath, rowKeyField,
 *       primaryTemplate, secondaryTemplate,
 *       voidScript?, voidKeyField?
 *     },
 *     closeAction?: { scriptName, label, confirmMessage?, fields?: [{ name, label, placeholder?, defaultFrom? }] }
 *       fields: scans taken with the close (e.g. the staging lane a trip is deposited to); each is
 *       required and is sent in _payload with the resolved row. defaultFrom names a key of the resolved
 *       row that fills the box to begin with (the trip's suggested lane), which a scan replaces.
 *   }
 */
const trimmed = (values) => Object.fromEntries(Object.entries(values).map(([k, v]) => [k, String(v ?? '').trim()]));

const RapidEntryOverlay = ({ config, rowData, colors, onClose, onSuccess }) => {
  const cfg = config || {};
  const resolverCfg = cfg.resolverPhase;
  const headerCfg = cfg.headerPhase;        // create/edit phase (alternative to resolverPhase)

  // Blind mode ("Blind Receive" etc, resolverPhase.blindActionButton) skips
  // resolution entirely -- there is no known parent record (no receiptId), so
  // headerSummary/progressPanel (both keyed off a resolved contextRow) do not
  // apply. cfg.blindEntry mirrors lineEntry/tally/closeAction's shape but
  // points at a receiptId-free submit script. contextRow becomes {} (not
  // null) in blind mode -- several effects below gate on `contextRow` being
  // truthy to load field metadata/run progress refresh, and {...{}} is a
  // harmless no-op when building the submit payload, whereas {...null} would
  // have skipped that metadata-loading effect entirely and rendered an empty form.
  const [isBlindMode, setIsBlindMode] = useState(false);
  const blindCfg = cfg.blindEntry || {};
  const summaryCfg = isBlindMode ? undefined : cfg.headerSummary;
  // headerSummary fields given as { name, hideWhenEmpty: true } show only when the record has a value (FH-410)
  const summaryHideEmpty = new Set((summaryCfg?.fields || [])
    .filter((f) => f && typeof f === 'object' && f.hideWhenEmpty).map((f) => f.name));
  const progressCfg = isBlindMode ? undefined : cfg.progressPanel;
  const lineCfg = isBlindMode ? blindCfg.lineEntry : cfg.lineEntry;
  const tallyCfg = isBlindMode ? blindCfg.tally : cfg.tally;
  const closeCfg = isBlindMode ? blindCfg.closeAction : cfg.closeAction;
  const [closeValues, setCloseValues] = useState({});

  // Phase initialization:
  //  - rowData present + headerPhase declared: start in headerPhase (edit mode)
  //  - rowData present + resolverPhase declared (no headerPhase): still run the
  //    resolver -- it's not just a lookup, it's what actually mints/attaches
  //    session state (e.g. a trip LPN via PickWork.OpenList()) that later scan-
  //    loop calls depend on. Skipping straight to 'scan' would leave that call
  //    unmade. The resolver's own field(s) get pre-filled from rowData below
  //    and auto-submitted once, so the user doesn't have to re-type anything.
  //  - rowData present, neither phase declared: jump straight to scan (no
  //    session-setup step exists to skip)
  //  - no rowData (global action): if resolverPhase or headerPhase declared, start in that phase; else jump to scan
  const initialPhase = (() => {
    if (rowData && headerCfg) return 'header';
    if (rowData && resolverCfg) return 'resolver';
    if (rowData) return 'scan';
    if (resolverCfg) return 'resolver';
    if (headerCfg) return 'header';
    return 'scan';
  })();
  const [phase, setPhase] = useState(initialPhase);
  const isEditMode = !!rowData;             // headerPhase update-mode trigger

  // Resolver state -- pre-filled from rowData when triggered from a row, so
  // the auto-run below (and the field, if ever shown) already has the value.
  const [resolverInput, setResolverInput] = useState(() => {
    const init = {};
    (resolverCfg?.fields || []).forEach((f) => { init[f.name] = rowData?.[f.name] ?? ''; });
    return init;
  });
  const autoResolvedRef = useRef(false);
  const [resolverMatches, setResolverMatches] = useState(null);
  const [resolverError, setResolverError] = useState(null);
  const [resolverLoading, setResolverLoading] = useState(false);

  // Header-phase state (create/edit form)
  const [headerPhaseFields, setHeaderPhaseFields] = useState([]);
  const [headerPhaseValues, setHeaderPhaseValues] = useState({});
  const headerValuesRef = useRef({});                          // latest header values, so a late validation answer for an old value is dropped
  headerValuesRef.current = headerPhaseValues;
  const [headerPhaseErrors, setHeaderPhaseErrors] = useState({});
  const [headerPhaseValidating, setHeaderPhaseValidating] = useState({});
  const [headerPhaseSubmitting, setHeaderPhaseSubmitting] = useState(false);
  const [headerPhaseError, setHeaderPhaseError] = useState(null);

  // Scan-loop state
  const [contextRow, setContextRow] = useState(rowData || null);
  // a close field's starting value from the resolved row (the trip's suggested lane); a scan replaces it
  useEffect(() => {
    const fields = (closeCfg?.fields || []).filter((f) => f.defaultFrom && contextRow?.[f.defaultFrom]);
    if (!fields.length) return;
    setCloseValues((v) => fields.reduce((acc, f) => (acc[f.name] ? acc : { ...acc, [f.name]: String(contextRow[f.defaultFrom]) }), v));
  }, [closeCfg, contextRow]);
  // Freshest copy of the header record, for the summary only (contextRow stays fixed so the
  // form isn't reset): a receive or void can change it, e.g. the receipt closing itself.
  const [headerRecord, setHeaderRecord] = useState(null);
  const [notice, setNotice] = useState(null);
  const [headerFields, setHeaderFields] = useState([]);
  const [lineFields, setLineFields] = useState([]);
  const [progressItems, setProgressItems] = useState([]);
  const [tallyRows, setTallyRows] = useState([]);
  const [lineValues, setLineValues] = useState({});
  const [itemFacts, setItemFacts] = useState({});
  // A fresh line's values: each field's own default, then lineEntry.contextDefaults that apply to this header record
  const lineDefaults = (fields) => {
    const defaults = {};
    fields.forEach((f) => { if (f.defaultValue) defaults[f.fieldName] = f.defaultValue; });
    (lineCfg?.contextDefaults || []).forEach((d) => {
      if (!isEmptyValue(contextRow?.[d.whenField])) Object.assign(defaults, d.set || {});
    });
    return defaults;
  };
  const lineValuesRef = useRef({});                            // latest line values, for validation Commands' formValues (FH-253)
  lineValuesRef.current = lineValues;
  const [submitting, setSubmitting] = useState(false);
  const [closing, setClosing] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitWarnings, setSubmitWarnings] = useState([]);
  const [dirty, setDirty] = useState(false);                   // a line was submitted but the close action (e.g. deposit) hasn't run
  const [loadingMeta, setLoadingMeta] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});         // { fieldName: 'message' }
  const [validating, setValidating] = useState({});            // { fieldName: true } while async validation runs
  const [dropdownOptions, setDropdownOptions] = useState({});  // { fieldName: [{value,label}] } loaded from a DROPDOWN field's optionsScript
  const [acOptions, setAcOptions] = useState({});              // { fieldName: [{value,label,...}] }
  const [acLoading, setAcLoading] = useState({});              // { fieldName: true } while options fetch
  const scanFieldRef = useRef(null);
  const acDebounceRef = useRef({});                            // { fieldName: timeoutId }
  const acSeqRef = useRef({});                                 // { fieldName: number } newest search per field; older answers are dropped
  const touchedFieldsRef = useRef(new Set());                  // fieldNames the user has manually edited this line -- defaultValueScript never overwrites these
  const defaultScriptDebounceRef = useRef(null);
  const [canCreate, setCanCreate] = useState({});              // { fieldName: true } where the user may create a missing record (createOption)
  const [createDialog, setCreateDialog] = useState(null);      // { f, typed, onChange, onApplyExtras, validateOnSelect } while the small create form is open
  // "Add custom field" (FH-202), where the config turns it on (headerPhase/lineEntry/headerSummary.customFields)
  const [mayAddField, setMayAddField] = useState({});         // { header: bool, line: bool }
  const [addFieldFor, setAddFieldFor] = useState(null);        // 'header' | 'line' while the dialog is open
  const [headerFieldsVersion, setHeaderFieldsVersion] = useState(0);
  const [lineFieldsVersion, setLineFieldsVersion] = useState(0);
  const addedFieldRef = useRef(null);                          // the field just added: scrolled to and focused once shown
  const optionsLoadedRef = useRef({ row: undefined, keys: new Set() }); // dropdown lists already asked for, per record

  // -----------------------------------------------------------------
  // Resolver
  // -----------------------------------------------------------------
  const runResolver = useCallback(async () => {
    const filled = Object.entries(resolverInput).filter(([, v]) => v && String(v).trim() !== '');
    if (filled.length === 0) {
      const names = (resolverCfg.fields || []).map((f) => f.label).join(', ');
      setResolverError(`Enter one of: ${names}`);
      return;
    }
    setResolverLoading(true);
    setResolverError(null);
    try {
      const resp = await FlexService.executeScript({
        scriptName: resolverCfg.scriptName,
        context: { _payload: resolverInput },
      });
      const ctx = resp?.context || {};
      const scriptSuccess = ctx.success;
      const matches = Array.isArray(ctx.result) ? ctx.result : (Array.isArray(ctx.matches) ? ctx.matches : []);
      if (!resp.success || scriptSuccess === false) {
        const diag = resp.diagnostics?.[0]?.message;
        setResolverError(ctx.error_message || diag || 'Failed to resolve');
        setResolverMatches([]);
        return;
      }
      if (matches.length === 0) {
        setResolverError('No matching records found.');
        setResolverMatches([]);
      } else if (matches.length === 1) {
        setContextRow(matches[0]);
        setPhase('scan');
      } else {
        setResolverMatches(matches);
      }
    } catch (err) {
      setResolverError(err.message || 'Resolver failed');
    } finally {
      setResolverLoading(false);
    }
  }, [resolverInput, resolverCfg]);

  // Row-triggered entry into a resolver-phase flow (e.g. a "Start List Pick"
  // row action tapping into the same flow as the header action): the pick
  // list id is already known from rowData, so auto-submit the resolver once
  // instead of making the user re-type/re-scan it. Runs at most once per
  // overlay open (autoResolvedRef), and only when every resolver field is
  // already populated from rowData -- if something's missing, fall back to
  // showing the resolver form as normal.
  useEffect(() => {
    if (phase !== 'resolver' || !rowData || !resolverCfg || autoResolvedRef.current) return;
    const allFieldsPresent = (resolverCfg.fields || []).every(
      (f) => resolverInput[f.name] && String(resolverInput[f.name]).trim() !== ''
    );
    if (!allFieldsPresent) return;
    autoResolvedRef.current = true;
    runResolver();
  }, [phase, rowData, resolverCfg, resolverInput, runResolver]);

  // -----------------------------------------------------------------
  // Header-phase (create/edit) — fetch entity field metadata + seed values
  // -----------------------------------------------------------------
  useEffect(() => {
    if (phase !== 'header' || !headerCfg?.entity) return;
    const token = localStorage.getItem('waide_access_token');
    (async () => {
      const res = await fetch(`/api/v1/catalog/${headerCfg.entity}/fields`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return;
      const data = await res.json();
      const allFields = data.fields || data || [];
      const wantedNames = await resolveVisibleFieldNames(headerCfg.fields || []);
      const listed = withCreateOptions(wantedNames
        .map((name) => allFields.find((f) => f.fieldName === name))
        .filter(Boolean), headerCfg.fields);
      const ordered = headerCfg.customFields ? withCustomFields(listed, allFields) : listed;
      setHeaderPhaseFields(ordered);

      // Seed: edit mode = pre-fill from rowData; create mode = defaults (TODAY for date defaults)
      const seed = {};
      ordered.forEach((f) => {
        let v = isEditMode ? (rowData?.[f.fieldName] ?? f.defaultValue ?? '') : (f.defaultValue ?? '');
        if (v === 'TODAY' && (f.fieldType === 'DATE' || f.fieldType === 'DATETIME')) {
          v = new Date().toISOString().split('T')[0];
        }
        // FH-303: a saved date comes back zoned ("2026-10-26T00:00:00Z"); the date box takes only "2026-10-26",
        // and left it blank, so the order's Ship Date looked lost on Edit. Its calendar date, no time zone shift.
        if (f.fieldType === 'DATE' && typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) v = v.slice(0, 10);
        seed[f.fieldName] = v;
      });
      // re-read after "Add custom field": what was already typed stays
      setHeaderPhaseValues((prev) => ({ ...seed, ...prev }));
    })();
  }, [phase, headerCfg, isEditMode, rowData, headerFieldsVersion]);

  // Async header-field validation (validationScript), shared by onBlur and submit.
  const validateHeaderField = useCallback(async (f, value) => {
    if (!f.validationScript) return true;
    if (value === '' || value == null) {
      // a cleared optional field has nothing to check: drop the error its old value had (FH-78)
      setHeaderPhaseErrors((p) => { if (!p[f.fieldName]) return p; const n = { ...p }; delete n[f.fieldName]; return n; });
      return true;
    }
    setHeaderPhaseValidating((p) => ({ ...p, [f.fieldName]: true }));
    try {
      const resp = await FlexService.executeScript({
        scriptName: f.validationScript,
        // formValues: the whole header, for rules that check one field against others (FH-253)
        context: { _payload: { [f.fieldName]: value }, fieldValue: value, fieldName: f.fieldName, formValues: { ...headerValuesRef.current, [f.fieldName]: value } },
      });
      const ctx = resp?.context || {};
      const ok = resp.success && ctx.success !== false;
      // the field changed while this was checked (e.g. cleared after a refusal): its answer is stale (FH-78)
      if ((headerValuesRef.current[f.fieldName] ?? '') !== value) return true;
      if (!ok) {
        setHeaderPhaseErrors((p) => ({ ...p, [f.fieldName]: ctx.error_message || `Invalid ${f.displayLabel}` }));
        return false;
      }
      setHeaderPhaseErrors((p) => { const n = { ...p }; delete n[f.fieldName]; return n; });
      return true;
    } catch (err) {
      setHeaderPhaseErrors((p) => ({ ...p, [f.fieldName]: err.message || 'Validation failed' }));
      return false;
    } finally {
      setHeaderPhaseValidating((p) => ({ ...p, [f.fieldName]: false }));
    }
  }, []);

  const submitHeaderPhase = useCallback(async () => {
    if (!headerCfg) return;
    // Required-field check
    const errs = {};
    headerPhaseFields.forEach((f) => {
      if (f.isRequired && f.isEditable !== false && isFieldShownFor(f, headerPhaseValues)) {
        const v = headerPhaseValues[f.fieldName];
        if (v === undefined || v === null || v === '') errs[f.fieldName] = `${f.displayLabel} is required`;
      }
    });
    if (Object.keys(errs).length > 0) {
      setHeaderPhaseErrors((prev) => ({ ...prev, ...errs }));
      setHeaderPhaseError('Please fill in all required fields.');
      return;
    }

    // Re-run async validations here rather than relying on the blur check: the
    // submit button is not disabled while a blur check is in flight (doing so
    // swallowed the click that caused the blur), so this is the real gate.
    // (blank ones too, so an error left from a value since cleared is dropped)
    const toValidate = headerPhaseFields.filter((f) => f.validationScript);
    if (toValidate.length > 0) {
      const results = await Promise.all(toValidate.map((f) => validateHeaderField(f, headerPhaseValues[f.fieldName])));
      if (results.some((r) => r === false)) {
        setHeaderPhaseError('Please fix the validation errors before submitting.');
        return;
      }
    }

    // Build payload: type-coerce based on field metadata, then in edit mode
    // preserve rowData and overlay edited values
    const coerced = coercePayload(headerPhaseFields, headerPhaseValues);
    const payload = foldCustomFields(isEditMode ? { ...rowData, ...coerced } : { ...coerced });
    const scriptName = isEditMode ? headerCfg.updateScript : headerCfg.createScript;
    if (!scriptName) {
      setHeaderPhaseError(`No ${isEditMode ? 'updateScript' : 'createScript'} configured.`);
      return;
    }

    setHeaderPhaseSubmitting(true);
    setHeaderPhaseError(null);
    try {
      const resp = await FlexService.executeScript({ scriptName, context: { _payload: payload } });
      const ctx = resp?.context || {};
      const matches = Array.isArray(ctx.result) ? ctx.result : (Array.isArray(ctx.matches) ? ctx.matches : []);
      if (!resp.success || ctx.success === false || matches.length === 0) {
        const diag = resp.diagnostics?.[0]?.message;
        setHeaderPhaseError(ctx.error_message || diag || `Failed to ${isEditMode ? 'update' : 'create'}`);
        return;
      }
      setContextRow(matches[0]);
      setPhase('scan');
    } catch (err) {
      setHeaderPhaseError(err.message || 'Header submit failed');
    } finally {
      setHeaderPhaseSubmitting(false);
    }
  }, [headerCfg, headerPhaseFields, headerPhaseValues, isEditMode, rowData, validateHeaderField]);

  // -----------------------------------------------------------------
  // Metadata + progress items once contextRow is known
  // -----------------------------------------------------------------
  useEffect(() => {
    if (phase !== 'scan' || !contextRow) return;
    const token = localStorage.getItem('waide_access_token');
    const fetchFields = async (entity) => {
      if (!entity) return [];
      const res = await fetch(`/api/v1/catalog/${entity}/fields`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) return [];
      const data = await res.json();
      return data.fields || data || [];
    };

    const rereading = !!addedFieldRef.current;
    const load = async () => {
      if (!rereading) setLoadingMeta(true); // re-reading after an add keeps the form on screen
      try {
        // Header summary fields (read-only display)
        const summaryFieldNames = await resolveVisibleFieldNames(summaryCfg?.fields || []);
        const allSummary = await fetchFields(summaryCfg?.entity);
        const summaryListed = summaryFieldNames.map((n) => allSummary.find((f) => f.fieldName === n)).filter(Boolean);
        setHeaderFields(summaryCfg?.customFields
          ? withCustomFields(summaryListed, allSummary.filter((f) => f.isVisibleInDetail !== false))
          : summaryListed);

        // Line-entry fields, in the order declared in config
        const lineFieldNames = await resolveVisibleFieldNames(lineCfg?.fields || []);
        const allLine = await fetchFields(lineCfg?.entity);
        const listedLine = withCreateOptions(lineFieldNames
          .map((name) => allLine.find((f) => f.fieldName === name))
          .filter(Boolean), lineCfg?.fields);
        let ordered = lineCfg?.customFields ? withCustomFields(listedLine, allLine) : listedLine;
        // FH-302: a line that creates another record also asks for that record's custom fields, e.g. Receive
        // Line asks for the received LPN's (Inventory) fields; a field defined as required shows * and is checked
        if (lineCfg?.customFieldsFrom) {
          // a field of that record named like one of the line's own fields is that field's answer (the stock's
          // Serial Number is the line's Serial Number), so it isn't asked a second time
          const own = new Set(ordered.map((f) => f.fieldName));
          const fromFields = (await fetchFields(lineCfg.customFieldsFrom))
            .filter((f) => !own.has(String(f.wmsPayloadPath || f.fieldName || '').replace(/^customField\./, '')));
          ordered = withCustomFields(ordered, fromFields);
        }
        setLineFields(ordered);

        // Seed defaults; a re-read after "Add custom field" keeps what was typed
        const defaults = lineDefaults(ordered);
        if (rereading) {
          setLineValues((prev) => ({ ...defaults, ...prev }));
        } else {
          touchedFieldsRef.current = new Set();
          setLineValues(defaults);
        }
      } finally {
        setLoadingMeta(false);
      }
    };
    load();
  }, [phase, contextRow, summaryCfg, lineCfg, lineFieldsVersion]);

  // Which createOption fields this user may actually create from (same check the server makes).
  useEffect(() => {
    const withOption = [...headerPhaseFields, ...lineFields].filter((f) => f.createOption);
    if (withOption.length === 0) return undefined;
    let cancelled = false;
    Promise.all(withOption.map(async (f) => [f.fieldName, await canCreateWith(f.createOption)]))
      .then((pairs) => { if (!cancelled) setCanCreate((prev) => ({ ...prev, ...Object.fromEntries(pairs) })); });
    return () => { cancelled = true; };
  }, [headerPhaseFields, lineFields]);

  // Who may add custom fields here, for each part of the form the config turns it on for
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      headerCfg?.customFields ? canAddCustomField(headerCfg.entity) : false,
      lineCfg?.customFields ? canAddCustomField(lineCfg.entity) : false,
    ]).then(([header, line]) => { if (!cancelled) setMayAddField({ header, line }); });
    return () => { cancelled = true; };
  }, [headerCfg, lineCfg]);

  // A field just added comes into view with the cursor in it (it's added after the last field)
  useEffect(() => {
    const name = addedFieldRef.current;
    if (!name || ![...headerPhaseFields, ...lineFields].some((f) => f.fieldName === name)) return;
    addedFieldRef.current = null;
    const box = document.querySelector(`[data-field="${name}"]`);
    if (box) {
      box.scrollIntoView({ block: 'center' });
      const input = box.querySelector('input, [role="combobox"]');
      if (input) input.focus();
    }
  }, [headerPhaseFields, lineFields]);

  // Options for DROPDOWN fields that come from an optionsScript (for example the inventory status list). The edit form
  // (EntityWizardOverlay) loads these, but this form only used fixed allowedValues, so the same field was empty here.
  useEffect(() => {
    // _entity tells a shared options script (custom_field_options) which entity the field is on
    const dropdownsWithScript = [
      // a custom field brought in from another record (FH-302: the received LPN's Inventory fields on
      // Receive Line) asks for its choices as that record's field
      ...lineFields.map((f) => ({ f, entity: isCustomField(f) && f.entityName ? f.entityName : lineCfg?.entity })),
      ...headerPhaseFields.map((f) => ({ f, entity: headerCfg?.entity })),
    ].filter(({ f }) => f.fieldType === 'DROPDOWN' && f.optionsScript && !f.optionsDependOn);   // those follow their fields (below)
    // Each list once per record: these fields re-render as the form moves between its header and
    // lines, and re-asking for the same lists each time ran into the server's request limit
    if (optionsLoadedRef.current.row !== contextRow) optionsLoadedRef.current = { row: contextRow, keys: new Set() };
    const loaded = optionsLoadedRef.current.keys;
    const toLoad = dropdownsWithScript.filter(({ f, entity }) => !loaded.has(`${entity}.${f.fieldName}`));
    if (toLoad.length === 0) return;
    toLoad.forEach(({ f, entity }) => loaded.add(`${entity}.${f.fieldName}`));
    const loadAll = async () => {
      const results = {};
      await Promise.all(
        toLoad.map(async ({ f, entity }) => {
          try {
            const resp = await FlexService.executeScript({
              scriptName: f.optionsScript,
              context: { _payload: { ...(contextRow || {}), _field: f.fieldName, _entity: entity } },
            });
            const ctx = resp?.context || {};
            const opts = ctx.result || ctx.data || [];
            if (resp?.success && Array.isArray(opts)) results[f.fieldName] = opts;
            else loaded.delete(`${entity}.${f.fieldName}`); // try again next time
          } catch (err) {
            loaded.delete(`${entity}.${f.fieldName}`);
            console.error(`Failed to load options from script "${f.optionsScript}" for field "${f.fieldName}":`, err);
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setDropdownOptions((prev) => ({ ...prev, ...results }));
      }
    };
    loadAll();
  }, [lineFields, headerPhaseFields, contextRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // Conditional (computed) defaults via defaultValueScript -- same plumbing as
  // optionsScript (FlexService.executeScript, same _payload convention) but
  // returns a single scalar via `result` instead of a list of {value,label}.
  // Re-runs (debounced) whenever any line value changes, so a field like
  // "default lot code" can react to the itemId the operator just scanned --
  // but only ever fills a field that is currently empty AND has not been
  // manually edited by the user (touchedFieldsRef), so it never clobbers
  // real input. Added 2026-09-24 to close the "no default value support for
  // custom fields" gap -- mirrors the exact reuse the user pointed out:
  // "we already have a way to bring a set of fixed values for a dropdown
  // (optionsScript) -- this is the same thing, just bringing 1 value."
  useEffect(() => {
    if (lineFields.length === 0) return;
    const scriptedFields = lineFields.filter((f) => f.defaultValueScript);
    if (scriptedFields.length === 0) return;

    if (defaultScriptDebounceRef.current) clearTimeout(defaultScriptDebounceRef.current);
    defaultScriptDebounceRef.current = setTimeout(async () => {
      const payload = { ...lineValues };
      const results = {};
      await Promise.all(
        scriptedFields.map(async (f) => {
          // Never recompute/overwrite a field the user already filled in or
          // manually edited -- a default is a convenience, not a correction.
          if (touchedFieldsRef.current.has(f.fieldName)) return;
          const current = lineValues[f.fieldName];
          if (current !== undefined && current !== null && current !== '') return;
          try {
            const response = await FlexService.executeScript({
              scriptName: f.defaultValueScript,
              context: { _payload: payload },
            });
            if (response.success) {
              const val = response.context?.result;
              if (val !== undefined && val !== null && val !== '') {
                results[f.fieldName] = val;
              }
            }
          } catch (err) {
            console.error(`Failed to compute default from script "${f.defaultValueScript}" for field "${f.fieldName}":`, err);
          }
        })
      );
      if (Object.keys(results).length > 0) {
        setLineValues((prev) => {
          // Re-check emptiness/touched against the LATEST state, not the
          // stale closure snapshot -- the user may have typed into one of
          // these fields while the script calls above were in flight.
          const merged = { ...prev };
          Object.entries(results).forEach(([fieldName, val]) => {
            if (touchedFieldsRef.current.has(fieldName)) return;
            const cur = merged[fieldName];
            if (cur === undefined || cur === null || cur === '') {
              merged[fieldName] = val;
            }
          });
          return merged;
        });
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, 300);
    return () => { if (defaultScriptDebounceRef.current) clearTimeout(defaultScriptDebounceRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineFields, lineValues]);

  // What the panel knows about the line's item (lineEntry.itemFacts), asked again when that field changes. Fields
  // show by these facts through visibleWhenField, like any other value of the line.
  const factsCfg = lineCfg?.itemFacts;
  const factsOn = factsCfg?.dependOn || 'itemId';
  const factsKey = factsCfg ? String(lineValues[factsOn] ?? '').trim() : '';
  useEffect(() => {
    if (!factsCfg?.script || !factsKey) {
      setItemFacts((prev) => (Object.keys(prev).length ? {} : prev));
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      try {
        const resp = await FlexService.executeScript({
          scriptName: factsCfg.script,
          context: { _payload: { ...(contextRow || {}), [factsOn]: factsKey } },
        });
        const facts = resp?.success ? resp?.context?.result : null;
        if (!cancelled) setItemFacts(facts && typeof facts === 'object' && !Array.isArray(facts) ? facts : {});
      } catch (err) {
        console.error(`Failed to read item facts from "${factsCfg.script}":`, err);
        if (!cancelled) setItemFacts({});
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [factsCfg?.script, factsOn, factsKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // the line's values with the item's facts: what decides which fields show
  const shownValues = useMemo(() => ({ ...lineValues, ...itemFacts }), [lineValues, itemFacts]);

  // A dropdown whose choices depend on other fields of the line (optionsDependOn, e.g. an order line's unit on its
  // item, FH-309) asks for them again whenever those fields change. A chosen value no longer offered gives way to the
  // first choice offered (the item's own unit).
  const dependentDropdowns = lineFields.filter((f) => f.fieldType === 'DROPDOWN' && f.optionsScript && f.optionsDependOn);
  const dependentKey = dependentDropdowns
    .map((f) => `${f.fieldName}:${f.optionsDependOn.split(',').map((d) => lineValues[d.trim()] ?? '').join('|')}`)
    .join(';');
  useEffect(() => {
    if (dependentDropdowns.length === 0) return undefined;
    let cancelled = false;
    const timer = setTimeout(async () => {
      const results = {};
      await Promise.all(dependentDropdowns.map(async (f) => {
        try {
          const resp = await FlexService.executeScript({
            scriptName: f.optionsScript,
            context: { _payload: { ...(contextRow || {}), ...lineValues, _field: f.fieldName, _entity: lineCfg?.entity } },
          });
          const opts = resp?.context?.result || resp?.context?.data || [];
          if (resp?.success && Array.isArray(opts)) results[f.fieldName] = opts;
        } catch (err) {
          console.error(`Failed to load options from script "${f.optionsScript}" for field "${f.fieldName}":`, err);
        }
      }));
      if (cancelled || Object.keys(results).length === 0) return;
      setDropdownOptions((prev) => ({ ...prev, ...results }));
      setLineValues((prev) => {
        const next = { ...prev };
        Object.entries(results).forEach(([name, opts]) => {
          if (opts.length > 0 && !opts.some((o) => String(o.value) === String(next[name]))) next[name] = opts[0].value;
        });
        return next;
      });
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentKey, lineFields.length]);

  // Progress items (refetched after every submit)
  const refreshProgress = useCallback(async () => {
    if (!progressCfg?.scriptName || !contextRow) return;
    try {
      const resp = await FlexService.executeScript({
        scriptName: progressCfg.scriptName,
        context: { _payload: contextRow },
      });
      const items = resp?.context?.result || [];
      setProgressItems(Array.isArray(items) ? items : []);
    } catch (err) {
      console.error('Progress refresh failed:', err);
    }
  }, [progressCfg, contextRow]);

  useEffect(() => { if (phase === 'scan') refreshProgress(); }, [phase, refreshProgress]);

  useEffect(() => { setHeaderRecord(null); }, [contextRow]);

  // Bring a new warning or result message into view (the void button may be scrolled far below it)
  const resultRef = useRef(null);
  useEffect(() => {
    if ((submitWarnings.length > 0 || notice) && resultRef.current?.scrollIntoView) {
      resultRef.current.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
  }, [submitWarnings, notice]);

  // Re-read the header record (headerSummary.refreshScript, looked up by refreshKeyField)
  const refreshHeader = useCallback(async () => {
    const script = summaryCfg?.refreshScript;
    const keyField = summaryCfg?.refreshKeyField;
    const key = contextRow?.[keyField];
    if (!script || !keyField || key == null || key === '') return;
    try {
      const resp = await FlexService.executeScript({ scriptName: script, context: { _payload: { [keyField]: key } } });
      const ctx = resp?.context || {};
      const rows = Array.isArray(ctx.result) ? ctx.result : (Array.isArray(ctx.matches) ? ctx.matches : []);
      const fresh = rows.find((r) => String(r?.[keyField]) === String(key));
      if (fresh) setHeaderRecord(fresh);
    } catch (err) {
      // The summary keeps showing what it had; the receive itself already succeeded.
    }
  }, [summaryCfg, contextRow]);

  // Tally initial preload — when tally.initialScript is configured, fetch existing
  // rows (e.g. existing receipt lines for the Edit Receipt flow) into the tally on
  // first entry to the scan phase. Subsequent submits append to whatever is here.
  useEffect(() => {
    if (phase !== 'scan' || !tallyCfg?.initialScript || !contextRow) return;
    (async () => {
      try {
        const resp = await FlexService.executeScript({
          scriptName: tallyCfg.initialScript,
          context: { _payload: contextRow },
        });
        const rows = resp?.context?.result || [];
        const limit = tallyCfg.limit || 100;
        if (Array.isArray(rows) && rows.length > 0) {
          setTallyRows(rows.slice(0, limit));
        }
      } catch (err) {
        console.error('Tally initial preload failed:', err);
      }
    })();
    // Only preload once on entering scan phase; submits / voids manage rows after that.
  }, [phase, contextRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------
  // Line submit
  // -----------------------------------------------------------------
  const resetForm = useCallback(() => {
    touchedFieldsRef.current = new Set();
    const defaults = lineDefaults(lineFields);
    // stickyFields (lineEntry config, e.g. receiving's "locationId"): carry the
    // just-submitted value forward instead of wiping it back to empty/default.
    // Real UX gap found live 2026-10-01: receiving multiple LPNs against the
    // same receipt into the same staging location meant re-picking that same
    // location on every single line -- the first LPN's choice should stick
    // until the user deliberately changes it, same receipt or not.
    (lineCfg?.stickyFields || []).forEach((fieldName) => {
      const current = lineValues[fieldName];
      if (current !== undefined && current !== '') {
        defaults[fieldName] = current;
        touchedFieldsRef.current.add(fieldName);
      }
    });
    setLineValues(defaults);
    setSubmitError(null);
    setFieldErrors({});
    setTimeout(() => { if (scanFieldRef.current) scanFieldRef.current.focus(); }, 50);
  }, [lineFields, lineCfg, lineValues, contextRow]); // eslint-disable-line react-hooks/exhaustive-deps

  // -----------------------------------------------------------------
  // Async field validation on blur (validationScript from field metadata)
  // -----------------------------------------------------------------
  const validateField = useCallback(async (field, value) => {
    if (!field.validationScript) return true;
    if (value === '' || value == null) {
      setFieldErrors((prev) => { if (!prev[field.fieldName]) return prev; const n = { ...prev }; delete n[field.fieldName]; return n; });
      return true;
    }
    setValidating((prev) => ({ ...prev, [field.fieldName]: true }));
    try {
      const resp = await FlexService.executeScript({
        scriptName: field.validationScript,
        context: {
          // Merge contextRow (parent PK / resolved receipt data, e.g. receiptId)
          // in with the field being validated -- some validation scripts need
          // more than just the one field (validate_item_on_receipt_line needs
          // receiptId to check the item against that receipt's expected lines).
          _payload: { ...contextRow, [field.fieldName]: value },
          fieldValue: value,
          fieldName: field.fieldName,
          formValues: { ...lineValuesRef.current, [field.fieldName]: value },
        },
      });
      const ctx = resp?.context || {};
      const ok = resp.success && ctx.success !== false;
      if (!ok) {
        const msg = ctx.error_message || resp.diagnostics?.[0]?.message || `Invalid ${field.displayLabel}`;
        setFieldErrors((prev) => ({ ...prev, [field.fieldName]: msg }));
        return false;
      }
      setFieldErrors((prev) => { const n = { ...prev }; delete n[field.fieldName]; return n; });
      return true;
    } catch (err) {
      setFieldErrors((prev) => ({ ...prev, [field.fieldName]: err.message || `Validation failed` }));
      return false;
    } finally {
      setValidating((prev) => ({ ...prev, [field.fieldName]: false }));
    }
  }, [contextRow]);

  // -----------------------------------------------------------------
  // Async option search for AUTOCOMPLETE fields (debounced typeahead)
  // -----------------------------------------------------------------
  const searchOptions = useCallback((field, searchText) => {
    if (!field.optionsScript) return;
    const fname = field.fieldName;
    // Debounce per-field
    if (acDebounceRef.current[fname]) clearTimeout(acDebounceRef.current[fname]);
    acDebounceRef.current[fname] = setTimeout(async () => {
      acDebounceRef.current[fname] = null; // no longer waiting (see onOpen)
      // Only the newest search may fill the list: an earlier, slower one (e.g. the blank
      // search fired when the field opened) answering last used to replace the typed or
      // scanned value's results with the unfiltered list.
      const seq = (acSeqRef.current[fname] || 0) + 1;
      acSeqRef.current[fname] = seq;
      setAcLoading((prev) => ({ ...prev, [fname]: true }));
      try {
        // Spread contextRow (the resolved header, e.g. Receipt -- has
        // receiptId) into _payload alongside search/limit -- added
        // 2026-09-24 so a receipt-scoped options script (e.g.
        // receipt_expected_items_search, which limits the Item autocomplete
        // to only items actually expected on the current receipt instead of
        // the whole catalog) can see which receipt is active. Previously
        // only { search, limit } was ever sent, so no options script here
        // could be receipt-aware at all.
        const resp = await FlexService.executeScript({
          scriptName: field.optionsScript,
          context: { _payload: { ...(contextRow || {}), search: searchText || '', limit: 20 } },
        });
        if (acSeqRef.current[fname] !== seq) return;
        const opts = resp?.context?.result || [];
        setAcOptions((prev) => ({ ...prev, [fname]: Array.isArray(opts) ? opts : [] }));
      } catch (err) {
        if (acSeqRef.current[fname] !== seq) return;
        console.error(`Search failed for ${fname}:`, err);
        setAcOptions((prev) => ({ ...prev, [fname]: [] }));
      } finally {
        if (acSeqRef.current[fname] === seq) setAcLoading((prev) => ({ ...prev, [fname]: false }));
      }
    }, 250);
  }, [contextRow]);

  // A picked option's other properties (e.g. a customer's ship-to address), for onApplyExtras.
  const optionExtras = (f, opt) => {
    const extras = {};
    Object.entries(opt || {}).forEach(([k, v]) => {
      if (k === 'value' || k === 'label' || k === 'score' || k === f.fieldName) return;
      extras[k] = v;
    });
    return extras;
  };

  // After a record is created from a field's "+ Create" entry, select it exactly as if it had
  // been picked from the list, so its extras (a new customer's ship-to address) fill in too.
  const selectCreatedRecord = useCallback(async ({ f, onChange, onApplyExtras, validateOnSelect }, newValue) => {
    onChange(newValue);
    if (f.optionsScript) {
      try {
        const resp = await FlexService.executeScript({
          scriptName: f.optionsScript,
          context: { _payload: { ...(contextRow || {}), search: newValue, limit: 20 } },
        });
        const opts = Array.isArray(resp?.context?.result) ? resp.context.result : [];
        setAcOptions((prev) => ({ ...prev, [f.fieldName]: opts }));
        const match = opts.find((o) => o?.value === newValue);
        const extras = match ? optionExtras(f, match) : {};
        if (onApplyExtras && Object.keys(extras).length > 0) onApplyExtras(extras);
      } catch (err) {
        // The value is already set; the auto-filled extras are only a convenience.
      }
    }
    if (f.validationScript && validateOnSelect) validateOnSelect(f, newValue);
  }, [contextRow]);

  // -----------------------------------------------------------------
  // Required-field validation (called before submit)
  // -----------------------------------------------------------------
  const validateRequired = useCallback(() => {
    const errs = {};
    lineFields.forEach((f) => {
      if (!f.isRequired) return;
      if (f.isEditable === false) return;
      if (!isFieldShownFor(f, shownValues)) return;   // a hidden question is not asked, so not required
      const v = lineValues[f.fieldName];
      if (v === undefined || v === null || v === '') {
        errs[f.fieldName] = `${f.displayLabel} is required`;
      }
    });
    setFieldErrors((prev) => ({ ...prev, ...errs }));
    return Object.keys(errs).length === 0;
  }, [lineFields, lineValues, shownValues]);

  const handleSubmitLine = useCallback(async (e) => {
    if (e?.preventDefault) e.preventDefault();

    // 1. Required-field check
    if (!validateRequired()) {
      setSubmitError('Please fill in all required fields.');
      return;
    }

    // 2. Re-run any async validations on fields that have a validationScript
    const fieldsWithValidation = lineFields.filter((f) => f.validationScript && lineValues[f.fieldName]);
    if (fieldsWithValidation.length > 0) {
      const results = await Promise.all(fieldsWithValidation.map((f) => validateField(f, lineValues[f.fieldName])));
      if (results.some((r) => r === false)) {
        setSubmitError('Please fix the validation errors before submitting.');
        return;
      }
    }

    setSubmitting(true);
    setSubmitError(null);
    setSubmitWarnings([]);
    setNotice(null);
    try {
      // Type-coerce line values based on field metadata before submit (NUMBER → Number,
      // DATE → ISO-8601 zoned timestamp, BOOLEAN → bool). See coerceFieldValue at top.
      const coercedLine = coercePayload(lineFields, lineValues);
      // a hidden field's answer no longer applies: it isn't sent (FH-316)
      lineFields.forEach((f) => { if (!isFieldShownFor(f, shownValues)) delete coercedLine[f.fieldName]; });
      // Pass the context row (contains the parent PK / scope) merged with the coerced line
      let base = contextRow;
      if (lineCfg.customFields || lineCfg.customFieldsFrom) {
        // the header's custom values (receipt fields) aren't the line's: send only the line's own
        base = Object.fromEntries(Object.entries(contextRow || {})
          .filter(([k]) => k !== 'customField' && !k.startsWith('customField.')));
      }
      const payload = foldCustomFields({ ...base, ...coercedLine });
      const resp = await FlexService.executeScript({
        scriptName: lineCfg.submitScript,
        context: { _payload: payload },
      });
      const ctx = resp?.context || {};
      const scriptSuccess = ctx.success;
      if (scriptSuccess === false || !resp.success) {
        const diag = resp.diagnostics?.[0]?.message;
        setSubmitError(ctx.error_message || diag || resp.errorMessage || 'Submit failed');
        return;
      }
      if (Array.isArray(ctx.warnings) && ctx.warnings.length > 0) {
        setSubmitWarnings(ctx.warnings);
      }

      // Tally: pull new rows from the configured response path. If the script wraps
      // the result under "result", we honor that — otherwise walk ctx directly.
      if (tallyCfg) {
        const root = ctx.result != null ? ctx.result : ctx;
        const newRows = getByPath(root, tallyCfg.responseRowsPath) || [];
        const limit = tallyCfg.limit || 10;
        const enrichedRows = (Array.isArray(newRows) ? newRows : [newRows])
          .filter(Boolean)
          .map((r) => ({ ...lineValues, ...r, _receivedAt: new Date().toLocaleTimeString() }));
        if (enrichedRows.length > 0) {
          setTallyRows((prev) => [...enrichedRows, ...prev].slice(0, limit));
        }
      }

      setDirty(true);
      await Promise.all([refreshProgress(), refreshHeader()]);
      if (lineCfg.repeatOnSubmit) resetForm();
    } catch (err) {
      setSubmitError(err.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  }, [lineValues, shownValues, contextRow, lineCfg, tallyCfg, refreshProgress, refreshHeader, resetForm, validateRequired, validateField, lineFields]);

  // -----------------------------------------------------------------
  // Void a tally row
  // -----------------------------------------------------------------
  const handleVoid = useCallback(async (row) => {
    if (!tallyCfg?.voidScript || !tallyCfg?.voidKeyField) return;
    const key = row[tallyCfg.voidKeyField];
    if (!key) return;
    const what = renderTemplate(tallyCfg.primaryTemplate, row);
    if (!window.confirm(`Void ${what}? This removes it as if it was never received.`)) return;
    setSubmitError(null);
    setNotice(null);
    try {
      const resp = await FlexService.executeScript({
        scriptName: tallyCfg.voidScript,
        context: { _payload: { [tallyCfg.voidKeyField]: key } },
      });
      const ctx = resp?.context || {};
      if (ctx.success === false || !resp.success) {
        const diag = resp.diagnostics?.[0]?.message;
        setSubmitError(ctx.error_message || diag || 'Void failed');
        return;
      }
      setTallyRows((prev) => prev.filter((r) => r[tallyCfg.voidKeyField] !== key));
      setNotice(typeof ctx.message === 'string' && ctx.message ? ctx.message : `Voided ${what}.`);
      setDirty(true);
      await Promise.all([refreshProgress(), refreshHeader()]);
    } catch (err) {
      setSubmitError(err.message || 'Void failed');
    }
  }, [tallyCfg, refreshProgress, refreshHeader]);

  // -----------------------------------------------------------------
  // Close action
  // -----------------------------------------------------------------
  const handleClose = useCallback(async () => {
    if (!closeCfg) return;
    const missing = (closeCfg.fields || []).find((f) => !String(closeValues[f.name] || '').trim());
    if (missing) {
      setSubmitError(`Scan the ${(missing.label || missing.name).toLowerCase()} first.`);
      return;
    }
    if (closeCfg.confirmMessage && !window.confirm(closeCfg.confirmMessage)) return;

    // closeAction.scriptName may be null/empty — in that case the Done button
    // just dismisses the overlay (no server call). Useful when no finalization
    // is needed (e.g. New Receipt where the receipt + lines are already saved).
    if (!closeCfg.scriptName) {
      onSuccess?.(closeCfg.successMessage || 'Done.');
      onClose?.();
      return;
    }

    setClosing(true);
    try {
      const resp = await FlexService.executeScript({
        scriptName: closeCfg.scriptName,
        context: { _payload: { ...contextRow, ...trimmed(closeValues) } },
      });
      const ctx = resp?.context || {};
      if (ctx.success === false || !resp.success) {
        const diag = resp.diagnostics?.[0]?.message;
        setSubmitError(ctx.error_message || diag || 'Close failed');
        return;
      }
      onSuccess?.(closeCfg.successMessage || 'Closed.');
      onClose?.();
    } catch (err) {
      setSubmitError(err.message || 'Close failed');
    } finally {
      setClosing(false);
    }
  }, [closeCfg, closeValues, contextRow, onClose, onSuccess]);

  // Guarded dismiss: when closeAction.guardOnClose is set and there is
  // un-finalized work (a line was submitted but the close action, e.g. deposit,
  // hasn't run), block the plain X close and show an error. The user must use
  // the close-action button (e.g. Deposit to Staging) to leave.
  const requestClose = useCallback(() => {
    if (closeCfg?.guardOnClose && dirty) {
      setSubmitError(closeCfg.discardMessage
        || 'You have picked items that are not yet deposited. Use the Deposit button before closing.');
      return;
    }
    onClose?.();
  }, [closeCfg, dirty, onClose]);

  // -----------------------------------------------------------------
  // Field renderers (line entry)
  // -----------------------------------------------------------------
  // -----------------------------------------------------------------
  // Generic field renderer — used by both line entry and header phase.
  // Caller passes the field, its current value, an onChange callback, and
  // optional onBlur/error/isValidating/isScan (scan-first auto-focus is
  // line-entry-only; header phase always passes isScan=false).
  // -----------------------------------------------------------------
  const renderField = ({ f, value, onChange, onBlur, error, isValidating, isScan, onApplyExtras, validateOnSelect = validateField }) => {
    const label = f.displayLabel + (f.isRequired ? ' *' : '');
    // No transient "Validating..." helper line: adding/removing it changed the field's
    // height while the blur check ran, shifting the submit button between mousedown
    // and mouseup so the click that caused the blur never landed ("click it twice").
    // Submit re-runs the validations itself, so nothing depends on seeing it.
    // The field's help text is always shown (an error replaces it), like the other entity
    // forms; it's there from first render, so it doesn't shift the layout on blur.
    const helperText = error || f.description;
    value = value || '';

    if (f.fieldType === 'AUTOCOMPLETE') {
      const options = acOptions[f.fieldName] || [];
      // Find current option by value so the input shows the value not the label
      const currentOption = options.find((o) => o.value === value) || null;
      // "+ Create ..." entry (FH-195): offered when the typed value matches no option, or the
      // list is empty, and only if this user may create that kind of record.
      const createOption = f.createOption && canCreate[f.fieldName] ? f.createOption : null;
      const filterOptions = (opts, state) => {
        const filtered = defaultFilterOptions(opts, state);
        if (!createOption) return filtered;
        const typed = (state.inputValue || '').trim();
        const exact = typed && opts.some((o) => String(o?.value ?? '').toUpperCase() === typed.toUpperCase());
        return exact ? filtered : [...filtered, createEntry(createOption, typed)];
      };
      return (
        <Autocomplete
          key={f.fieldName}
          freeSolo
          size="small"
          options={options}
          filterOptions={filterOptions}
          loading={!!acLoading[f.fieldName]}
          value={currentOption || value || ''}
          getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (isCreateEntry(opt) ? opt.value : (opt?.label || opt?.value || '')))}
          renderOption={(props, opt) => (isCreateEntry(opt)
            ? <li {...props} key="__create" style={{ color: '#22c55e', fontWeight: 600 }}>{opt.label}</li>
            : <li {...props} key={opt.value}>{opt.label || opt.value}</li>)}
          isOptionEqualToValue={(o, v) => {
            const ov = typeof o === 'string' ? o : o?.value;
            const vv = typeof v === 'string' ? v : v?.value;
            return ov === vv;
          }}
          onChange={(_, newVal) => {
            if (isCreateEntry(newVal)) {
              // Keep what was typed in the field, and open the small create form with it.
              onChange(newVal.value);
              setCreateDialog({ f, typed: newVal.value, onChange, onApplyExtras, validateOnSelect });
              return;
            }
            if (newVal && typeof newVal === 'object') {
              // User picked an option from the dropdown — set this field's value AND
              // apply any extra fields on the option to matching form fields. This is
              // how customer/owner/carrier search options auto-fill related fields
              // (customerName, shipToName, shipToAddress*, etc.). The component-level
              // onApplyExtras handler filters to known form field names.
              onChange(newVal.value);
              if (onApplyExtras) {
                const extras = optionExtras(f, newVal);
                if (Object.keys(extras).length > 0) onApplyExtras(extras);
              }
              // A real option was picked from the list -- validate the fresh value
              // immediately rather than relying on the field's onBlur prop (a
              // stale closure over the pre-selection value that also depends on a
              // native blur event MUI may not fire after a click-select).
              if (f.validationScript) validateOnSelect(f, newVal.value);
            } else {
              // freeSolo: user typed and committed without picking an option
              onChange(newVal == null ? '' : newVal);
            }
          }}
          onInputChange={(_, inputVal, reason) => {
            if (reason === 'input') {
              onChange(inputVal);
              searchOptions(f, inputVal);
            }
          }}
          // Opening the list loads it only when no search is already waiting: text pasted (or typed
          // in one go) into the closed field starts its own search first, and a blank search here
          // used to replace it, so the list showed everything except what was typed.
          onOpen={() => {
            if (acDebounceRef.current[f.fieldName]) return;
            if ((acOptions[f.fieldName] || []).length === 0) searchOptions(f, value || '');
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              placeholder={f.placeholder || 'Type to search...'}
              onBlur={onBlur}
              error={!!error}
              helperText={helperText}
              inputRef={isScan ? scanFieldRef : undefined}
              autoFocus={isScan}
              sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
            />
          )}
        />
      );
    }

    if (f.fieldType === 'DROPDOWN') {
      const staticOptions = f.allowedValues
        ? (typeof f.allowedValues === 'string' ? JSON.parse(f.allowedValues) : f.allowedValues)
        : [];
      // options from the field's optionsScript win when it has one (fixed allowedValues are the fallback)
      const options = (f.optionsScript && dropdownOptions[f.fieldName]) || staticOptions;
      // labelId ties the visible label to the select, so it has an accessible name (FH-184)
      const labelId = `rapid-entry-${f.fieldName}-label`;
      return (
        <FormControl key={f.fieldName} fullWidth size="small" sx={{ mb: 1.5 }} error={!!error}>
          <InputLabel id={labelId}>{label}</InputLabel>
          <Select
            labelId={labelId}
            value={value}
            label={label}
            onChange={(e) => onChange(e.target.value)}
            onBlur={onBlur}
            sx={{ borderRadius: 0 }}
          >
            {options.map((opt) => {
              const v = typeof opt === 'object' ? (opt.value || opt.code || opt.optionValue) : opt;
              const l = typeof opt === 'object' ? (opt.label || opt.displayName || v) : opt;
              return <MenuItem key={v} value={v}>{l}</MenuItem>;
            })}
          </Select>
          {(helperText) && (<Typography variant="caption" sx={{ color: error ? '#ef4444' : 'text.secondary', mt: 0.5 }}>{helperText}</Typography>)}
        </FormControl>
      );
    }
    if (f.fieldType === 'BOOLEAN') {
      // a Yes/No field (e.g. one a customer added, FH-202) is a checkbox, as on the other forms
      return (
        <Box key={f.fieldName} sx={{ mb: 1.5 }}>
          <FormControlLabel
            control={<Checkbox checked={value === true || value === 'true'} onChange={(e) => onChange(e.target.checked)} />}
            label={f.displayLabel}
          />
          {helperText && <FormHelperText error={!!error} sx={{ mt: -0.5, ml: 4 }}>{helperText}</FormHelperText>}
        </Box>
      );
    }
    if (f.fieldType === 'NUMBER') {
      return (
        <TextField
          key={f.fieldName}
          fullWidth size="small" type="number"
          label={label} value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          error={!!error}
          helperText={helperText}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
        />
      );
    }
    if (f.fieldType === 'DATE') {
      return (
        <TextField
          key={f.fieldName}
          fullWidth size="small" type="date"
          label={label} value={value}
          onChange={(e) => onChange(e.target.value)}
          onBlur={onBlur}
          error={!!error}
          helperText={helperText}
          InputLabelProps={{ shrink: true }}
          sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
        />
      );
    }
    return (
      <TextField
        key={f.fieldName}
        fullWidth size="small"
        label={label}
        placeholder={isScan ? (f.placeholder || 'Scan or leave blank') : f.placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        error={!!error}
        helperText={helperText}
        inputRef={isScan ? scanFieldRef : undefined}
        autoFocus={isScan}
        inputProps={isScan ? { autoCapitalize: 'off', autoCorrect: 'off', spellCheck: false } : undefined}
        sx={{
          mb: 1.5,
          '& .MuiOutlinedInput-root': { borderRadius: 0 },
          ...(isScan ? { '& .MuiInputBase-input': { fontFamily: 'monospace' } } : {}),
        }}
      />
    );
  };

  // Thin wrapper: line entry uses lineValues/fieldErrors/validating state
  const renderLineField = (f) => {
    const value = lineValues[f.fieldName];
    const onChange = (v) => {
      // entity_field_metadata.text_transform (e.g. UPPERCASE on itemId/LPN/
      // locationId-style code fields) was never applied anywhere in this
      // component -- confirmed live 2026-09-23: a real BlindReceiveInventoryLine
      // .associatedLpn row correctly had text_transform='UPPERCASE' set, but
      // the actual submitted LPN still went out lowercase, since nothing here
      // ever read/applied it. Affects every rapid-entry flow using this
      // component (normal receiving included), not just blind receiving.
      const transformed = (f.textTransform === 'UPPERCASE' && typeof v === 'string') ? v.toUpperCase() : v;
      // Mark as manually touched so the defaultValueScript effect never
      // overwrites this field again for the rest of this line, even if the
      // user clears it back to empty (a real, deliberate clear should stay
      // cleared, not silently get re-filled on the next debounce tick).
      touchedFieldsRef.current.add(f.fieldName);
      setLineValues((prev) => ({ ...prev, [f.fieldName]: transformed }));
      // the "fill in all required fields" banner was about the empty fields: it goes once one is filled in
      setSubmitError((prev) => (prev === 'Please fill in all required fields.' ? null : prev));
      setFieldErrors((prev) => {
        if (!prev[f.fieldName]) return prev;
        if (f.validationScript && transformed !== '' && transformed != null) return prev;
        const n = { ...prev }; delete n[f.fieldName]; return n;
      });
    };
    // Auto-fill: copy extras from a picked option onto other line fields whose
    // names match. Limited to known line field names so spurious keys don't
    // leak into the submit payload.
    const onApplyExtras = (extras) => {
      const known = new Set(lineFields.map((lf) => lf.fieldName));
      const patch = {};
      Object.entries(extras).forEach(([k, v]) => {
        if (known.has(k) && v !== undefined && v !== null && v !== '') patch[k] = v;
      });
      if (Object.keys(patch).length > 0) {
        // A picked autocomplete option is a deliberate user choice, same as
        // typing -- treat it as touched so defaultValueScript never overwrites it.
        Object.keys(patch).forEach((k) => touchedFieldsRef.current.add(k));
        setLineValues((prev) => ({ ...prev, ...patch }));
      }
    };
    return renderField({
      f, value, onChange,
      onBlur: () => { if (f.validationScript) validateField(f, value); },
      error: fieldErrors[f.fieldName],
      isValidating: !!validating[f.fieldName],
      isScan: f.fieldType === 'SCAN' || f.fieldName === lineCfg?.scanFirst,
      onApplyExtras,
    });
  };

  // Thin wrapper: header phase uses headerPhaseValues/headerPhaseErrors state.
  // Validation is required-field-only for now (async validationScript on header
  // fields is also honored if declared in field metadata).
  const renderHeaderPhaseField = (f) => {
    const value = headerPhaseValues[f.fieldName];
    const onChange = (v) => {
      const transformed = (f.textTransform === 'UPPERCASE' && typeof v === 'string') ? v.toUpperCase() : v;
      setHeaderPhaseValues((prev) => ({ ...prev, [f.fieldName]: transformed }));
      setHeaderPhaseErrors((prev) => {
        if (!prev[f.fieldName]) return prev;
        // an emptied field has nothing left to check (FH-78); a new value waits for its blur check
        if (f.validationScript && transformed !== '' && transformed != null) return prev;
        const n = { ...prev }; delete n[f.fieldName]; return n;
      });
    };
    const onBlur = () => { validateHeaderField(f, value); };
    // Auto-fill: when an AUTOCOMPLETE option is picked, copy extras to other
    // header fields with matching names — e.g. picking a customer auto-fills
    // customerName, shipToName, shipToAddress1, shipToCity, etc. Filtered to
    // known header field names to keep the submit payload tight.
    const onApplyExtras = (extras) => {
      const known = new Set(headerPhaseFields.map((hf) => hf.fieldName));
      const patch = {};
      Object.entries(extras).forEach(([k, v]) => {
        if (known.has(k) && v !== undefined && v !== null && v !== '') patch[k] = v;
      });
      if (Object.keys(patch).length > 0) {
        setHeaderPhaseValues((prev) => ({ ...prev, ...patch }));
      }
    };
    return renderField({
      f, value, onChange, onBlur,
      error: headerPhaseErrors[f.fieldName],
      isValidating: !!headerPhaseValidating[f.fieldName],
      isScan: false,
      onApplyExtras,
      // Picking an option must report into the header's own error state (the default,
      // validateField, writes line-entry errors the header never shows).
      validateOnSelect: validateHeaderField,
    });
  };

  // -----------------------------------------------------------------
  // Phase renderers
  // -----------------------------------------------------------------
  const renderResolverPhase = () => {
    const fields = resolverCfg.fields || [];
    const blindBtn = resolverCfg.blindActionButton;
    const md = resolverCfg.matchDisplay || {};
    return (
      <Box sx={{ p: 4, maxWidth: 560 }}
        {...enterAdvanceProps(() => { if (!resolverLoading) runResolver(); }, { anyOne: true })}>
        {resolverCfg.instructions && (
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 3 }}>
            {resolverCfg.instructions}
          </Typography>
        )}
        {fields.map((f) => (
          <TextField
            key={f.name}
            fullWidth size="small"
            label={f.label}
            placeholder={f.placeholder}
            value={resolverInput[f.name] || ''}
            onChange={(e) => setResolverInput((prev) => ({ ...prev, [f.name]: e.target.value }))}
            sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}
          />
        ))}
        {resolverError && <Alert severity="warning" sx={{ mb: 2 }}>{resolverError}</Alert>}
        {resolverMatches && resolverMatches.length > 1 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ color: colors.textMuted, mb: 1, display: 'block' }}>
              Multiple matches — pick one:
            </Typography>
            {resolverMatches.map((m, i) => (
              <Box
                key={getByPath(m, md.primaryField) || i}
                onClick={() => { setContextRow(m); setPhase('scan'); }}
                sx={{
                  p: 1.5, mb: 0.5, border: `1px solid ${colors.border}`, cursor: 'pointer',
                  '&:hover': { bgcolor: 'rgba(34, 197, 94, 0.08)' },
                }}
              >
                <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
                  {md.primaryField ? getByPath(m, md.primaryField) : `Match ${i + 1}`}
                </Typography>
                {md.subtitleTemplate && (
                  <Typography variant="caption" sx={{ color: colors.textMuted }}>
                    {renderTemplate(md.subtitleTemplate, m)}
                  </Typography>
                )}
              </Box>
            ))}
          </Box>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="contained"
            startIcon={resolverLoading ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PlayArrowIcon />}
            onClick={runResolver}
            disabled={resolverLoading}
            sx={{ bgcolor: '#22c55e', borderRadius: 0, textTransform: 'none', '&:hover': { bgcolor: '#16a34a' } }}
          >
            {resolverLoading ? 'Resolving...' : (resolverCfg.buttonLabel || 'Continue')}
          </Button>
          {blindBtn && (
            <Tooltip title={blindBtn.tooltip || ''}>
              <span>
                <Button
                  variant="outlined"
                  disabled={blindBtn.enabled === false}
                  onClick={() => { setIsBlindMode(true); setContextRow({}); setPhase('scan'); }}
                  sx={{ borderRadius: 0, textTransform: 'none', color: colors.textSecondary, borderColor: colors.border }}
                >
                  {blindBtn.label || 'Skip'}
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>
      </Box>
    );
  };

  const renderHeaderPhase = () => {
    const title = headerCfg?.title || (isEditMode ? 'Edit' : 'New');
    const instr = headerCfg?.instructions;
    const buttonLabel = isEditMode
      ? (headerCfg?.updateButtonLabel || 'Update')
      : (headerCfg?.createButtonLabel || 'Create');
    const submitting = headerPhaseSubmitting;
    const skipLabel = headerCfg?.skipButtonLabel || 'Continue to Lines';

    // "Continue to Lines" — only available in edit mode, since create mode must
    // call the createScript to get a contextRow with a primary key. Edit mode
    // already has rowData, so the user can jump straight to the line loop
    // without persisting header changes.
    const skipToLines = () => {
      setContextRow(rowData);
      setPhase('scan');
    };

    return (
      <Box sx={{ p: 4, maxWidth: 640 }}
        {...enterAdvanceProps(() => { if (!headerPhaseSubmitting) submitHeaderPhase(); })}>
        <Typography variant="overline" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>{title}</Typography>
        {instr && (
          <Typography variant="body2" sx={{ color: colors.textSecondary, mb: 2 }}>{instr}</Typography>
        )}
        {headerPhaseFields.filter((f) => isFieldShownFor(f, headerPhaseValues)).map((f) => (
          <Box key={f.fieldName} data-field={f.fieldName}>{renderHeaderPhaseField(f)}</Box>
        ))}
        {mayAddField.header && (
          <Button size="small" startIcon={<AddIcon />} onClick={() => setAddFieldFor('header')}
            sx={{ textTransform: 'none', color: colors.textSecondary, mb: 1 }}>
            Add custom field
          </Button>
        )}
        {headerPhaseError && <Alert severity="warning" sx={{ mb: 2 }}>{headerPhaseError}</Alert>}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <Button
            variant="contained"
            startIcon={submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : <PlayArrowIcon />}
            onClick={submitHeaderPhase}
            // Not disabled while a blur validation is in flight: pressing this button is
            // what blurs the field, and disabling it mid-press swallowed the click.
            // submitHeaderPhase re-runs the validations itself.
            disabled={submitting || Object.keys(headerPhaseErrors).length > 0}
            sx={{
              bgcolor: '#22c55e', borderRadius: 0, textTransform: 'none',
              '&:hover': { bgcolor: '#16a34a' },
              '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted },
            }}
          >
            {submitting ? 'Saving...' : buttonLabel}
          </Button>
          {isEditMode && (
            <Button
              variant="outlined"
              onClick={skipToLines}
              disabled={submitting}
              sx={{
                borderRadius: 0, textTransform: 'none',
                color: colors.textSecondary, borderColor: colors.border,
                '&:hover': { borderColor: '#22c55e', color: '#22c55e' },
              }}
            >
              {skipLabel}
            </Button>
          )}
        </Box>
      </Box>
    );
  };

  const renderHeaderSummary = () => (
    <Box
      sx={{
        display: 'flex', gap: 3, p: 2, borderBottom: `1px solid ${colors.border}`,
        bgcolor: colors.surfaceElevated, flexWrap: 'wrap',
      }}
    >
      {headerFields.filter((f) => !summaryHideEmpty.has(f.fieldName)
        || !isEmptyValue(summaryValue(headerRecord || contextRow, f))).map((f) => (
        <Box key={f.fieldName}>
          <Typography variant="caption" sx={{ color: colors.textMuted, fontSize: '0.65rem', textTransform: 'uppercase' }}>
            {f.displayLabel}
          </Typography>
          <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
            {formatSummaryValue(f, summaryValue(headerRecord || contextRow, f))}
          </Typography>
        </Box>
      ))}
    </Box>
  );

  const renderProgressPanel = () => {
    if (!progressCfg) return null;
    const keyField = progressCfg.itemKeyField;
    const displayField = progressCfg.displayField;
    const expectedField = progressCfg.expectedField;
    const receivedField = progressCfg.receivedField;
    // Optional -- the progress panel's data (receipt_expected_lines, etc.) already
    // carries uom, but there was never a config slot to show it, so "40 / 200" never
    // said the unit. Config-driven and optional so panels with no meaningful UOM
    // (e.g. a non-inventory progress list) aren't forced to show a blank unit.
    const uomField = progressCfg.uomField;
    return (
      <Box sx={{ p: 2, borderLeft: `1px solid ${colors.border}`, minWidth: 280, maxWidth: 320, overflow: 'auto' }}>
        <Typography variant="overline" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
          {progressCfg.title || 'Progress'}
        </Typography>
        {progressItems.length === 0 && (
          <Typography variant="caption" sx={{ color: colors.textMuted }}>No items.</Typography>
        )}
        {progressItems.map((item, i) => {
          const expected = Number(item?.[expectedField]) || 0;
          const received = Number(item?.[receivedField]) || 0;
          const pct = expected > 0 ? Math.min(100, (received / expected) * 100) : 0;
          const over = received > expected && expected > 0;
          const done = received >= expected && expected > 0;
          return (
            <Box key={item?.[keyField] || i} sx={{ mb: 1.5 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                  {item?.[displayField]}
                </Typography>
                {done && <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} />}
              </Box>
              <Typography variant="caption" sx={{ color: over ? '#f59e0b' : colors.textMuted }}>
                {received} / {expected}{uomField && item?.[uomField] ? ` ${item[uomField]}` : ''} {over && '(over)'}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                  mt: 0.5, height: 4, borderRadius: 0,
                  '& .MuiLinearProgress-bar': { bgcolor: over ? '#f59e0b' : (done ? '#22c55e' : '#3b82f6') },
                }}
              />
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderTally = ({ inline = true } = {}) => {
    if (!tallyCfg) return null;
    // When tally is on the right (not inline below the form) show even when empty,
    // so the operator sees the "Lines (0)" label as soon as they enter the loop.
    if (inline && tallyRows.length === 0) return null;
    return (
      <>
        {inline && <Divider sx={{ my: 2, borderColor: colors.border }} />}
        <Typography variant="overline" sx={{ color: colors.textMuted, display: 'block', mb: 1 }}>
          {tallyCfg.title || 'Recent'} ({tallyRows.length})
        </Typography>
        {!inline && tallyRows.length === 0 && (
          <Typography variant="caption" sx={{ color: colors.textMuted }}>None yet.</Typography>
        )}
        {tallyRows.map((row, i) => {
          const key = row[tallyCfg.rowKeyField] || i;
          const primary = renderTemplate(tallyCfg.primaryTemplate, row);
          const secondary = renderTemplate(tallyCfg.secondaryTemplate, { ...row, receivedAt: row._receivedAt });
          const canVoid = !!(tallyCfg.voidScript && tallyCfg.voidKeyField && row[tallyCfg.voidKeyField]);
          return (
            <Box
              key={key}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1, mb: 0.5, bgcolor: colors.surfaceElevated, fontSize: '0.8rem',
              }}
            >
              <Box>
                <Typography variant="body2" sx={{ color: colors.textPrimary, fontWeight: 500 }}>
                  {primary}
                </Typography>
                <Typography variant="caption" sx={{ color: colors.textMuted, fontFamily: 'monospace' }}>
                  {secondary}
                </Typography>
              </Box>
              {canVoid && (
                <Tooltip title="Void this row">
                  <IconButton size="small" onClick={() => handleVoid(row)}>
                    <DeleteOutlineIcon fontSize="small" sx={{ color: colors.textMuted, '&:hover': { color: '#ef4444' } }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
          );
        })}
      </>
    );
  };

  const renderScanPhase = () => (
    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {renderHeaderSummary()}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        <Box sx={{ flex: 1, p: 3, maxWidth: 560, overflow: 'auto' }}>
          {loadingMeta && <CircularProgress size={20} sx={{ color: '#22c55e' }} />}
          {!loadingMeta && (
            <Box component="form" onSubmit={handleSubmitLine}
              {...enterAdvanceProps(() => { if (!submitting) handleSubmitLine(); })}>
              {/* Results of the last receive / void sit above the fields: after a receive the form
                  resets to its first field, so a warning by the button was off screen (FH-193) */}
              <Box ref={resultRef}>
                {submitWarnings.length > 0 && (
                  <Alert severity="warning" onClose={() => setSubmitWarnings([])} sx={{ mb: 2 }}>
                    <ul style={{ margin: 0, paddingLeft: 18 }}>
                      {submitWarnings.map((w, i) => <li key={i}>{w}</li>)}
                    </ul>
                  </Alert>
                )}
                {notice && <Alert severity="success" onClose={() => setNotice(null)} sx={{ mb: 2 }}>{notice}</Alert>}
              </Box>
              {lineFields.filter((f) => isFieldShownFor(f, shownValues)).map((f) => (
                <Box key={f.fieldName} data-field={f.fieldName}>{renderLineField(f)}</Box>
              ))}
              {mayAddField.line && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => setAddFieldFor('line')}
                  sx={{ textTransform: 'none', color: colors.textSecondary, mb: 1 }}>
                  Add custom field
                </Button>
              )}

              {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

              <Button
                type="submit"
                fullWidth variant="contained"
                // Not disabled while a blur validation is in flight (that swallowed the
                // click that caused the blur -- "Receive Line needs two clicks");
                // handleSubmitLine re-runs the validations itself.
                disabled={submitting || Object.keys(fieldErrors).length > 0}
                startIcon={submitting ? <CircularProgress size={16} sx={{ color: 'inherit' }} /> : null}
                sx={{
                  bgcolor: '#22c55e', borderRadius: 0, textTransform: 'none',
                  '&:hover': { bgcolor: '#16a34a' },
                  '&.Mui-disabled': { bgcolor: colors.surfaceElevated, color: colors.textMuted },
                }}
              >
                {submitting ? 'Submitting...' : (lineCfg.submitLabel || 'Submit')}
              </Button>
            </Box>
          )}
          {/* Render tally below the form only when the right column is occupied by
              the progress panel. Otherwise tally takes the right column itself. */}
          {progressCfg && renderTally({ inline: true })}
        </Box>
        {progressCfg
          ? renderProgressPanel()
          : tallyCfg && (
              <Box sx={{ p: 2, borderLeft: `1px solid ${colors.border}`, minWidth: 280, maxWidth: 360, overflow: 'auto' }}>
                {renderTally({ inline: false })}
              </Box>
            )}
      </Box>
    </Box>
  );

  // Title: what the user is doing (the button they clicked), never "Rapid Entry"/"Entry" (FH-234),
  // plus the record being worked on once there is one.
  const titleBase = cfg.actionLabel || 'Enter details';
  const primaryField = resolverCfg?.matchDisplay?.primaryField || headerCfg?.matchDisplay?.primaryField;
  const titlePrimary = contextRow && primaryField ? getByPath(contextRow, primaryField) : null;

  return (
    <Box
      sx={{
        position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10,
        bgcolor: colors.background, display: 'flex', flexDirection: 'column',
      }}
    >
      <Box
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2, py: 1.5, borderBottom: `1px solid ${colors.border}`, bgcolor: colors.surface,
        }}
      >
        <Typography variant="subtitle1" sx={{ color: colors.textPrimary, fontWeight: 600 }}>
          {phase === 'resolver' ? (resolverCfg?.title || titleBase)
            : phase === 'header' ? (headerCfg?.title || cfg.actionLabel || (isEditMode ? 'Edit' : 'New'))
            : (titlePrimary ? `${titleBase} — ${titlePrimary}` : titleBase)}
          {phase === 'scan' && contextRow && resolverCfg?.matchDisplay?.subtitleTemplate && (
            <Typography component="span" variant="body2" sx={{ color: colors.textSecondary, ml: 1.5 }}>
              {/* the header as re-read after each line, so a status that changed (fully received: CLOSE) shows here too */}
              {renderTemplate(resolverCfg.matchDisplay.subtitleTemplate, { ...contextRow, ...(headerRecord || {}) })}
            </Typography>
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {phase === 'scan' && (closeCfg?.fields || []).map((f) => (
            <TextField
              key={f.name}
              size="small"
              label={f.label || f.name}
              placeholder={f.placeholder}
              value={closeValues[f.name] || ''}
              onChange={(e) => setCloseValues((v) => ({ ...v, [f.name]: e.target.value }))}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleClose(); } }}
              inputProps={{ 'data-testid': `close-field-${f.name}` }}
              sx={{ width: 180 }}
            />
          ))}
          {phase === 'scan' && closeCfg && (
            <Button
              variant="outlined"
              startIcon={closing ? <CircularProgress size={14} /> : <DoneAllIcon />}
              onClick={handleClose}
              disabled={closing}
              sx={{ borderRadius: 0, textTransform: 'none', color: '#22c55e', borderColor: '#22c55e' }}
            >
              {closing ? 'Closing...' : (closeCfg.label || 'Close')}
            </Button>
          )}
          <IconButton onClick={requestClose} aria-label="Close" sx={{ color: colors.textSecondary }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </Box>
      <Box sx={{ flex: 1, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {phase === 'resolver' && renderResolverPhase()}
        {phase === 'header' && renderHeaderPhase()}
        {phase === 'scan' && renderScanPhase()}
      </Box>
      <AddCustomFieldDialog
        open={!!addFieldFor}
        entityName={addFieldFor === 'line' ? lineCfg?.entity : headerCfg?.entity}
        colors={colors}
        onCancel={() => setAddFieldFor(null)}
        onAdded={(added) => {
          addedFieldRef.current = added.fieldName || null;
          if (addFieldFor === 'line') setLineFieldsVersion((v) => v + 1);
          else setHeaderFieldsVersion((v) => v + 1);
          setAddFieldFor(null);
        }}
      />
      <InlineCreateDialog
        open={!!createDialog}
        option={createDialog?.f.createOption}
        initialValue={createDialog?.typed}
        colors={colors}
        onCancel={() => setCreateDialog(null)}
        onCreated={(newValue) => {
          const target = createDialog;
          setCreateDialog(null);
          if (target) selectCreatedRecord(target, newValue);
        }}
      />
    </Box>
  );
};

export default RapidEntryOverlay;
